<template lang="pug">
  yz-dialog(v-if="showDialog" v-model="showDialog" fullscreen persistent scrollable)
    yz-card
      yz-dialog-fullscreen-header(:title="isEdit ? $t('panel.edit_bulk_panel_claim') : $t('panel.create_bulk_panel_claim')" :cancel-action-label="$t('cancel')" :save-action-label="isEdit ? $t('save') : $t('panel.create_claim')" :loading="isLoading" :disable-save="isError || isFetching" @back="onCloseDialog" @cancel="onCloseDialog" @save="onSaveClaim")
      yz-dialog-fullscreen-content.pa-6
        // Loading state (full width)
        template(v-if="isFetching")
          create-panel-claim-loader

        // Error state (full width)
        template(v-else-if="isError")
          yz-empty-state-card(icon="$yzi-alert-triangle" :title="$t('panel.unable_to_load_invoices')" :description="$t('panel.unable_to_load_invoices_description')" border-color="yz-danger-500" icon-color="yz-danger-500" :action-label="$t('panel.try_again')" @action-click="fetchAllInvoices")

        // Main content (show when not loading and no error)
        template(v-else)
          v-form(ref="form")
            v-row(no-gutters style="min-height: calc(100dvh - 60px - 48px);")
              // Left Column - Selected Invoices
              v-col.text-neutral-700(cols="7" xl="8")
                .yz-heading-9 {{ $t('panel.selected_invoices') }}
                .yz-regular-body-xs {{ $t('panel.review_invoices_bulk_claim') }}

                // Selected Invoices Table
                yz-card.mt-6(outlined)
                  .pa-4
                    yz-data-table(:headers="selectedInvoicesHeaders" :items="invoicesData" :items-per-page="pageSize" :page="currentPage" :loading="isTableLoading")
                      template(v-slot:item.visitDate="{ item }")
                        .yz-regular-body-xs.font-medium {{ item.visitDate.date }}
                        .yz-regular-body-xs {{ item.visitDate.time }}

                      template(v-slot:item.invoiceNo="{ item }")
                        .yz-regular-body-xs {{ item.invoiceNo }}

                      template(v-slot:item.staffDetails="{ item }")
                        .yz-regular-body-xs.font-medium {{ item.staffName }}
                        .d-flex.align-center
                          .yz-regular-body-xs(v-if="item.staffId") {{ item.staffId }}
                          yz-badge.ml-1(v-if="item.staffBadge" border, border-color="#0C6DFF", radius="12px" text-color="#0C6DFF" height="16px")
                            span.yz-medium-body-2xs {{ item.staffBadge }}

                      template(v-slot:item.patientDetails="{ item }")
                        .py-2
                          .yz-medium-body-sm {{ item.patientName }}
                          .yz-regular-body-xs.text-neutral-500(v-if="item.patientId") {{ item.patientId }}
                          .yz-regular-body-xs.text-neutral-500(v-if="item.chitNo") Chit no.: {{ item.chitNo }}

                      template(v-slot:item.mcDays="{ item }")
                        .yz-medium-body-sm.text-center {{ item.mcDays }}

                      template(v-slot:item.diagnosis="{ item }")
                        .yz-medium-body-sm {{ item.diagnosis }}

                      template(v-slot:item.treatments="{ item, index }")
                        yz-tooltip(:direction="index === 0 ? 'bottom': 'right'" max-width="600px")
                          yz-badge(border size="sm") {{ item.treatments }}
                          template(#tooltip)
                            div.mb-3(v-for="(treatmentItems, categoryName) in item.treatmentDetails" :key="categoryName")
                              .yz-heading-9.text-white.mb-1 {{ categoryName }}
                              .yz-regular-body-sm.text-white(v-for="treatment in treatmentItems" :key="treatment") {{ treatment }}

                      template(v-slot:item.total="{ item }")
                        .yz-medium-body-sm {{ item.amount | displayPrice($currency) }}

                      template(v-slot:footer)
                        yz-divider.my-0
                        // Total summary and pagination
                        .d-flex.justify-space-between.align-center.py-2.px-3
                          .yz-regular-body-xs.no-wrap
                            | Total:
                            span.yz-medium-body-sm.ml-1 {{ totalAmount | displayPrice($currency) }}
                            |  ({{ totalCount }} {{ totalCount === 1 ? 'invoice' : 'invoices' }})
                          yz-pagination(v-model="currentPage" :page-size="pageSize" :count="totalCount" type="item" arrow, @input="fetchAllInvoices")

              // Divider Column
              yz-divider.mx-6(vertical)

              // Right Column - Bulk Claim Details
              v-col.text-neutral-700
                .yz-heading-9 {{ $t('panel.panel_claim_details') }}
                .yz-regular-body-xs {{ $t('panel.key_information_submitted_claim') }}

                // Panel summary card
                yz-card.mt-6.pa-3(color="yz-neutral-300" outlined style="border-radius: 10px;")
                  // Header row - Panel name with claim number badge
                  v-row.mb-2(no-gutters, align="start")
                    v-col(cols="7")
                      v-row(no-gutters, align="center")
                        v-col(cols="auto")
                          .yz-medium-body-sm.text-neutral-700 {{ effectivePanelName }}
                        yz-divider.mx-1(v-if="isEdit" vertical, color="yz-neutral-400")
                        v-col(v-if="isEdit")
                          .yz-medium-body-sm.text-neutral-500 {{ $t('panel.claim') }} {{ '#' + parentClaim.rid }}
                      .d-flex.align-center.gap-2.mt-1
                        .yz-heading-6.text-neutral-700 {{ totalAmount | displayPrice($currency) }}
                        yz-btn(v-if="isEdit && currentClaimStatus === 'submitted'" icon="$yzi-edit-05" icon-only size="md" color="yz-neutral-500" @click="onEditAmount")
                        claim-status-badge(v-if="isEdit && parentClaim.status === 'received'" :status="parentClaim.status" size="sm" dot-size="6px")

                    v-col(v-if="isEdit && parentClaim.status !== 'received'")
                      // Status dropdown row
                      .yz-medium-body-sm.text-neutral-700 {{ $t('panel.claim_status') }}
                      yz-dropdown.mt-1(v-model="currentClaimStatus" :items="statusOptions" size="lg")

                template(v-if="parentClaim && parentClaim.status === 'received'")
                  // Payment summary section for received status
                  received-payment-summary-card.mt-6(:admin-fee="editedData.adminFee" :rejected-amount="editedData.rejectedAmount" :received-amount="receivedAmountDisplay" :received-date="editedData.date")

                template(v-else)
                  // Record deductions section
                  .mt-6(v-if="isEdit && (currentClaimStatus === 'approved' || currentClaimStatus === 'received')")
                    .yz-medium-body-xs.text-neutral-500.mb-2 {{ $t('panel.record_deductions') }}:
                    admin-fee(:admin-fee-value.sync="editedData.adminFee" :admin-fee-type.sync="editedData.adminFeeType" :admin-fee-percentage.sync="editedData.adminFeePercentage" :base-amount="adminFeeBaseAmount" :readonly="currentClaimStatus === 'received' && parentClaim.status === 'approved'")
                    rejected-amount(v-model="editedData.rejectedAmount" :balance-assigned="balanceAssigned" :readonly="currentClaimStatus === 'received' && parentClaim.status === 'approved'" @assign-balance="onAssignBalance" @updated="onRejectedAmountUpdated")

                    // Status amount summary
                    .d-flex.justify-space-between.align-center.mt-5
                      .yz-medium-body-sm.text-neutral-700.font-semibold {{ amountLabelByStatus }}
                      .yz-medium-body-sm.text-success-500.font-semibold {{ finalAmountAfterDeductions | displayPrice($currency) }}

                  // Form fields
                  .mt-6
                    .yz-medium-body-sm.text-neutral-700.mb-1 {{ dateLabelByStatus }}
                    yz-text-field(v-model="formattedDate" :placeholder="datePickerPlaceholder" prepend-inner-icon="$yzi-calendar" readonly clickable clearable size="lg" required :rules="dateRules" :required-error-message="$t('panel.date_required')" @click="showDatePickerDialog = true" @click:clear="editedData.date = null")

                .mt-6
                  .yz-medium-body-sm.text-neutral-700.mb-1 {{ $t('panel.remarks') }}
                  yz-textarea(v-model="editedData.remarks" :placeholder="$t('panel.write_remarks_here')" rows="4")

                yz-divider.my-6

                // Upload photo section
                .yz-medium-body-xs.text-neutral-700.mb-4 {{ $t('panel.upload_photo') }}
                yz-upload-card(v-model="uploadedPhotos" multiple, append-files, accept="image/*" :primary-title="$t('panel.click_to_upload')" :secondary-title="$t('panel.or_drag_and_drop')" :description="$t('panel.jpg_png_max_5mb')" upload-mode="immediate" @upload-batch-complete="onUploadBatchCompleted")

                // QR code section
                .d-flex.mt-4.gap-4.align-center
                  yz-upload-qr-databin(:qr-size="100" @databin-created="onQrCreated" @error="onQrError")
                  div
                    .yz-medium-body-xs.text-neutral-700.mb-1 {{ $t('panel.add_from_other_devices') }}
                    .yz-regular-body-xs.text-neutral-700 {{ $t('panel.scan_qr_upload_description') }}
                // Databin synchronization component (hidden)
                yz-databin-sync(v-if="databinKey" v-model="allSyncedFiles" :added-items.sync="uploadedPhotos" :databin-key="databinKey" @error="onDatabinError" ref="databinSync")

                // Preview files
                .mt-4
                  yz-file-preview(v-model="originalFiles" :to-delete-items.sync="originalFileToDelete" :truncate-length="10")
                  yz-file-preview(v-model="allSyncedFiles" :to-delete-items.sync="fileToDelete" :truncate-length="10" @deleted="onDeletedFiles")

          // Bulk Assign Balance Dialog
          bulk-assign-balance-dialog(v-if="showBulkAssignBalanceDialog" v-model="showBulkAssignBalanceDialog" :parent-claim="parentClaim" :rejected-amount="editedData.rejectedAmount" @confirmed="onBulkAssignBalanceConfirmed" @cancel="onBulkAssignBalanceCanceled")

          // Assign Balance Confirmation Dialog
          assign-balance-confirmation-dialog(v-if="showAssignBalanceDialog" v-model="showAssignBalanceDialog" type="bulk" :rejected-amount="editedData.rejectedAmount" @confirmed="onBulkAssignBalanceConfirmed" @canceled="onBulkAssignBalanceCanceled")

          // Edit Bulk Claim Amount Dialog
          edit-bulk-claim-amount-dialog(v-if="showEditAmountDialog" v-model="showEditAmountDialog" :parent-claim="parentClaim" @confirmed="onEditClaimAmountConfirmed")

          // Change Claim Status Confirmation Dialog
          change-claim-status-confirmation-dialog(v-if="showChangeStatusDialog" v-model="showChangeStatusDialog" :current-status="parentClaim.status" :new-status="currentClaimStatus" :admin-fee="editedData.adminFee" :rejected-amount="editedData.rejectedAmount" @confirmed="onChangeStatusConfirmed" @canceled="onChangeStatusCanceled")

          // Date Picker Dialog
          yz-dialog(v-model="showDatePickerDialog" max-width="380px")
            yz-card
              yz-dialog-normal-content.px-2.pt-2.pb-0
                yz-date-picker(v-model="editedData.date" scrollable, @change="showDatePickerDialog = false")
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { DateTime } from 'luxon'
import EditBulkClaimAmountDialog from '@/components/clinic/panel/dialogs/bulk/EditBulkClaimAmountDialog.vue'
import AssignBalanceConfirmationDialog from '@/components/clinic/panel/dialogs/AssignBalanceConfirmationDialog.vue'
import BulkAssignBalanceDialog from '@/components/clinic/panel/dialogs/bulk/BulkAssignBalanceDialog.vue'
import ChangeClaimStatusConfirmationDialog from '@/components/clinic/panel/dialogs/ChangeClaimStatusConfirmationDialog.vue'
import AdminFee from '@/components/clinic/panel/claims/AdminFee.vue'
import RejectedAmount from '@/components/clinic/panel/claims/RejectedAmount.vue'
import CreatePanelClaimLoader from '@/components/clinic/panel/claims/loader/CreatePanelClaimLoader.vue'
import ClaimStatusBadge from '@/components/clinic/panel/claims/ClaimStatusBadge.vue'
import ReceivedPaymentSummaryCard from '@/components/clinic/panel/claims/ReceivedPaymentSummaryCard.vue'

export default {
  components: { EditBulkClaimAmountDialog, AssignBalanceConfirmationDialog, BulkAssignBalanceDialog, ChangeClaimStatusConfirmationDialog, AdminFee, RejectedAmount, CreatePanelClaimLoader, ClaimStatusBadge, ReceivedPaymentSummaryCard },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    parentInvoices: {
      type: Array,
      default: () => []
    },
    parentClaim: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      // Page states
      isLoading: false,
      isFetching: true,
      isTableLoading: false,
      isError: false,
      isInitialized: false,
      // Dialogs
      showEditAmountDialog: false,
      showBulkAssignBalanceDialog: false,
      showAssignBalanceDialog: false,
      showChangeStatusDialog: false,
      showDatePickerDialog: false,
      // Form data
      currentClaimStatus: 'submitted',
      editedData: {
        date: null,
        remarks: '',
        editClaimAmount: { value: null, remarks: null, invoices: [] },
        adminFee: null,
        adminFeeType: 'fixed',
        adminFeePercentage: null,
        rejectedAmount: null,
        receivedAmount: null,
        assignBalance: []
      },
      // Databin & files
      databinKey: null,
      allSyncedFiles: [],
      uploadedPhotos: [],
      fileToDelete: [],
      originalFiles: [],
      originalFileToDelete: [],
      // Pagination
      totalCount: 0,
      currentPage: 1,
      pageSize: 10,
      // Table headers for selected invoices
      selectedInvoicesHeaders: [],
      // Invoice data from API
      invoicesData: []
    }
  },
  mounted() {
    // Initialize table headers with i18n
    this.selectedInvoicesHeaders = [
      { text: this.$t('panel.visit_date'), value: 'visitDate', sortable: false, width: '140px' },
      { text: this.$t('panel.invoice_no'), value: 'invoiceNo', sortable: false, width: '105px' },
      { text: this.$t('panel.staff_details'), value: 'staffDetails', sortable: false },
      { text: this.$t('panel.patient_details'), value: 'patientDetails', sortable: false },
      { text: this.$t('panel.mc_days'), value: 'mcDays', sortable: false, align: 'center' },
      { text: this.$t('panel.diagnosis'), value: 'diagnosis', sortable: false },
      { text: this.$t('panel.treatments'), value: 'treatments', sortable: false },
      { text: this.$t('panel.total'), value: 'total', align: 'end', sortable: false }
    ]

    // Check if we have invoices to fetch - either from parentInvoices (create mode) or parentClaim.invoices (edit mode)
    const hasInvoicesToFetch = (this.parentInvoices && this.parentInvoices.length > 0) || (this.parentClaim && this.parentClaim.invoices && this.parentClaim.invoices.length > 0)

    if (hasInvoicesToFetch) {
      this.fetchAllInvoices()
    } else {
      this.isError = true
      this.isFetching = false
    }

    // Set date for both edit and create modes
    this.editedData.date = this.getDateByStatus()

    // Set mode-specific data for edit mode
    if (this.isEdit) {
      this.editedData.remarks = this.parentClaim.remarks
      this.originalFiles = this.parentClaim.documents || []
      this.currentClaimStatus = this.parentClaim.status

      this.editedData.adminFee = this.parentClaim.admin_fee || null
      this.editedData.adminFeeType = this.parentClaim.admin_fee_percentage ? 'percentage' : 'fixed'
      this.editedData.adminFeePercentage = this.parentClaim.admin_fee_percentage || null
      this.editedData.rejectedAmount = this.parentClaim.rejected_amount || null
      this.editedData.receivedAmount = this.parentClaim.received_amount || null
    }

    // Mark as initialized after all setup is complete
    // Use nextTick to ensure all watchers have processed initial values
    this.$nextTick(() => {
      this.isInitialized = true
    })
  },
  watch: {
    currentClaimStatus: {
      handler() {
        this.handleClaimStatusChange()
      },
      immediate: true
    },
    needToAssignBalance(newValue, oldValue) {
      // Only auto-open dialog when transitioning from false to true after initialization
      if (this.isInitialized && !oldValue && newValue) {
        this.showBulkAssignBalanceDialog = true
      }
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    showDialog: {
      get() {
        return this.value
      },
      set(newValue) {
        this.$emit('input', newValue)
      }
    },
    isEdit() {
      return !!this.parentClaim
    },
    statusOptions() {
      const baseOptions = [
        { text: this.$t('panel.status_submitted'), value: 'submitted' },
        { text: this.$t('panel.status_approved'), value: 'approved' },
        { text: this.$t('panel.status_rejected'), value: 'rejected' }
      ]

      if (this.isEdit && this.parentClaim && this.parentClaim.status !== 'rejected') {
        baseOptions.push({ text: this.$t('panel.status_received'), value: 'received' })
      }

      return baseOptions
    },
    totalAmount() {
      if (!this.isEdit) {
        // Create mode - sum directly from parentInvoices prop for immediate display
        return this.parentInvoices.reduce((sum, invoice) => sum + (invoice.amount || 0), 0)
      }

      // Edit mode - use displayAmount
      return this.displayAmount
    },
    displayAmount() {
      if (!this.isEdit || !this.parentClaim) return 0

      const status = (this.currentClaimStatus || '').toLowerCase()

      // Prioritize editedData.editClaimAmount.value if it exists and status is "submitted"
      if (status === 'submitted' && this.editedData.editClaimAmount.value !== null) {
        return this.editedData.editClaimAmount.value
      }

      // Status transition edge cases
      if ((this.parentClaim.status === 'approved' && status === 'rejected') || (this.parentClaim.status === 'rejected' && status === 'approved')) {
        return this.parentClaim.submitted_amount
      }

      if (this.parentClaim.status === 'approved' && status === 'received') {
        return this.parentClaim.approved_amount
      }

      const statusAmountMap = {
        submitted: this.parentClaim.submitted_amount,
        approved: this.parentClaim.approved_amount,
        rejected: this.parentClaim.rejected_amount,
        received: this.parentClaim.received_amount
      }

      return statusAmountMap[status] || this.parentClaim.submitted_amount
    },
    finalAmountAfterDeductions() {
      if (!this.isEdit || !this.parentClaim) return 0

      const baseAmount = this.parentClaim.submitted_amount || 0
      const rejectedAmount = this.editedData.rejectedAmount || 0

      // For 'approved' status: only deduct rejected amount (no admin fee)
      if (this.currentClaimStatus === 'approved') {
        return baseAmount - rejectedAmount
      }

      // For 'received' or 'rejected' status: deduct both admin fee and rejected amount
      const adminFee = this.editedData.adminFee || 0
      return baseAmount - adminFee - rejectedAmount
    },
    adminFeeBaseAmount() {
      if (!this.isEdit || !this.parentClaim) return 0
      const submittedAmount = this.parentClaim.submitted_amount || 0
      const rejectedAmount = this.editedData.rejectedAmount || 0
      return submittedAmount - rejectedAmount
    },
    needToAssignBalance() {
      if (!this.isEdit || !this.parentClaim) return false

      const rejectedAmountChanged = this.parentClaim.rejected_amount !== this.editedData.rejectedAmount && this.editedData.rejectedAmount !== null
      const isApproveOrReceived = this.currentClaimStatus === 'approved' || this.currentClaimStatus === 'received'

      return rejectedAmountChanged && isApproveOrReceived
    },
    balanceAssigned() {
      // If we don't need to assign balance (rejected amount unchanged), consider it assigned
      if (!this.needToAssignBalance && this.editedData.rejectedAmount !== null && (this.currentClaimStatus === 'approved' || this.currentClaimStatus === 'received')) {
        return true
      }

      // If user has manually assigned via dialog
      return this.editedData.assignBalance.length > 0
    },
    receivedAmountDisplay() {
      return this.editedData.receivedAmount || this.parentClaim?.received_amount || 0
    },
    dateLabelByStatus() {
      return this.getLabelByStatus('date', 'Submitted date')
    },
    amountLabelByStatus() {
      return this.getLabelByStatus('amount', 'Submitted amount')
    },
    panelBreakdown() {
      const breakdown = {}

      this.invoicesData.forEach(invoice => {
        const panelName = invoice.panelName || 'N/A'
        if (!breakdown[panelName]) {
          breakdown[panelName] = {
            name: panelName,
            count: 0,
            total: 0
          }
        }
        breakdown[panelName].count++
        breakdown[panelName].total += invoice.amount || 0
      })

      return Object.values(breakdown)
    },
    primaryPanel() {
      if (this.panelBreakdown.length === 1) {
        const panel = this.panelBreakdown[0]
        return panel.name !== 'N/A' ? panel.name : null
      }
      return null
    },
    effectivePanelName() {
      // In edit mode, use the panel name from parentClaim
      if (this.isEdit && this.parentClaim && this.parentClaim.panel) {
        return this.parentClaim.panel.name
      }
      // In create mode, use primaryPanel or show "Multiple Panels"
      return this.primaryPanel || this.$t('panel.multiple_panels')
    },
    effectivePanelId() {
      // In edit mode, use the panel ID from parentClaim
      if (this.isEdit && this.parentClaim && this.parentClaim.panel) {
        return this.parentClaim.panel.id
      }
      // In create mode, get panel ID from first invoice
      return this.parentInvoices[0]?.panel?.id || null
    },
    formattedDate() {
      if (!this.editedData.date) return ''
      return DateTime.fromISO(this.editedData.date).toFormat('dd/MM/yyyy')
    },
    dateRules() {
      return [v => !!v || this.$t('error_message.date_is_required')]
    },
    datePickerPlaceholder() {
      if (!this.isEdit || !this.parentClaim) {
        return this.$t('panel.select_submitted_date')
      }

      const statusPlaceholderMap = {
        submitted: this.$t('panel.select_submitted_date'),
        approved: this.$t('panel.select_approved_date'),
        rejected: this.$t('panel.select_rejected_date'),
        received: this.$t('panel.select_received_date')
      }

      return statusPlaceholderMap[this.currentClaimStatus] || this.$t('panel.select_submitted_date')
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    // Helper methods
    getLabelByStatus(suffix, defaultLabel) {
      if (!this.isEdit || !this.parentClaim) return defaultLabel

      const statusLabelMap = {
        submitted: `${this.$t('panel.status_submitted')} ${suffix}`,
        approved: `${this.$t('panel.status_approved')} ${suffix}`,
        rejected: `${this.$t('panel.status_rejected')} ${suffix}`,
        received: `${this.$t('panel.status_received')} ${suffix}`
      }

      return statusLabelMap[this.currentClaimStatus] || defaultLabel
    },
    getDateByStatus() {
      if (!this.isEdit || !this.parentClaim) {
        return DateTime.local().toFormat('yyyy-MM-dd')
      }

      const statusDateMap = {
        submitted: this.parentClaim.submitted_date,
        approved: this.parentClaim.approved_date,
        rejected: this.parentClaim.rejected_date,
        received: this.parentClaim.received_date
      }

      return statusDateMap[this.currentClaimStatus]
    },
    // Status change handlers
    handleClaimStatusChange() {
      this.editedData.date = this.getDateByStatus()
      this.editedData.editClaimAmount = { value: null, remarks: null }

      if (this.currentClaimStatus === this.parentClaim?.status) {
        this.revertToOriginalStatus()
        return
      }

      this.handleStatusTransition()
    },
    revertToOriginalStatus() {
      this.editedData.adminFee = this.parentClaim.admin_fee
      this.editedData.adminFeeType = this.parentClaim.admin_fee_percentage ? 'percentage' : 'fixed'
      this.editedData.adminFeePercentage = this.parentClaim.admin_fee_percentage || null
      this.editedData.rejectedAmount = this.parentClaim.rejected_amount
      this.editedData.receivedAmount = this.parentClaim.received_amount

      const needsBalance = (this.currentClaimStatus === 'approved' || this.currentClaimStatus === 'received') && this.parentClaim.rejected_amount

      this.editedData.assignBalance = needsBalance ? this.setAssignBalance(this.parentClaim.rejected_amount) : []
    },
    handleStatusTransition() {
      if (this.currentClaimStatus === 'rejected') {
        this.handleTransitionToRejected()
        return
      }

      if (this.currentClaimStatus === 'received') {
        this.handleTransitionToReceived()
        return
      }

      this.resetDeductions()
      this.editedData.assignBalance = []
    },
    handleTransitionToRejected() {
      this.resetDeductions()
      this.editedData.assignBalance = this.setAssignBalance(this.parentClaim.submitted_amount)
    },
    handleTransitionToReceived() {
      if (this.parentClaim.status === 'submitted') {
        this.handleSubmittedToReceived()
        return
      }
      if (this.parentClaim.status === 'approved') {
        this.handleApprovedToReceived()
        return
      }
      this.resetDeductions()
      this.editedData.assignBalance = []
    },
    handleSubmittedToReceived() {
      this.resetDeductions()
      this.editedData.receivedAmount = this.parentClaim.submitted_amount
      this.editedData.assignBalance = []
    },
    handleApprovedToReceived() {
      // Revert to original parentClaim values (readonly behavior)
      this.editedData.adminFee = this.parentClaim.admin_fee || null
      this.editedData.adminFeeType = this.parentClaim.admin_fee_percentage ? 'percentage' : 'fixed'
      this.editedData.adminFeePercentage = this.parentClaim.admin_fee_percentage || null
      this.editedData.rejectedAmount = this.parentClaim.rejected_amount || null

      this.editedData.receivedAmount = this.parentClaim.approved_amount

      // When rejected amount exists, consider balance already assigned from approved stage
      if (this.parentClaim.rejected_amount !== null) {
        this.editedData.assignBalance = this.setAssignBalance(this.parentClaim.rejected_amount)
      } else {
        this.editedData.assignBalance = []
      }
    },
    resetDeductions() {
      this.editedData.adminFee = null
      this.editedData.adminFeeType = 'fixed'
      this.editedData.adminFeePercentage = null
      this.editedData.rejectedAmount = null
      this.editedData.receivedAmount = null
    },
    setAssignBalance(rejectedAmount) {
      if (!rejectedAmount) return []

      // For bulk claims, return array structure from BulkAssignBalanceDialog
      // This will be populated by the dialog
      return this.editedData.assignBalance.length > 0 ? this.editedData.assignBalance : []
    },
    sanitizeData(invoice) {
      // Format visit date into separate date and time objects
      let visitDate = { date: '', time: '' }
      if (invoice.visit_date) {
        try {
          const dt = DateTime.fromISO(invoice.visit_date)
          visitDate = {
            date: dt.toFormat('dd MMMM yyyy'),
            time: dt.toFormat('h:mma').toLowerCase()
          }
        } catch (error) {
          console.error('Error formatting date:', error)
          visitDate = { date: invoice.visit_date, time: '' }
        }
      }

      // Group treatments by type
      const treatmentDetails = {}
      const treatmentTypes = {
        consultation: 'Consultation',
        service: 'Service',
        procedure: 'Procedure',
        medication: 'Medication',
        investigation: 'Investigation',
        document: 'Document',
        package: 'Package'
      }

      if (invoice.treatments && Array.isArray(invoice.treatments)) {
        invoice.treatments.forEach(treatment => {
          const categoryName = treatmentTypes[treatment.type] || 'Other'
          if (!treatmentDetails[categoryName]) {
            treatmentDetails[categoryName] = []
          }
          treatmentDetails[categoryName].push(treatment.name)
        })
      }

      // Format diagnosis - join array or take first item
      let diagnosis = ''
      if (invoice.diagnosis && Array.isArray(invoice.diagnosis) && invoice.diagnosis.length > 0) {
        diagnosis = invoice.diagnosis.join(', ')
      }

      return {
        uid: invoice.uid,
        visitDate,
        invoiceNo: invoice.invoice_no?.toString() || '',
        staffName: invoice.staff_name || '',
        staffId: invoice.staff_ic || '',
        staffBadge: invoice.relationship || '',
        patientName: invoice.name || '',
        patientId: invoice.ic || '',
        chitNo: invoice.chit_number || '',
        panelName: invoice.panel?.name || '',
        mcDays: invoice.mc_days || 0,
        diagnosis,
        treatments: `${invoice.treatments?.length || 0} items`,
        treatmentDetails,
        amount: invoice.amount || 0,
        originalInvoice: invoice
      }
    },
    async fetchAllInvoices() {
      // Use full page loading only for initial load, table loading for pagination
      if (this.invoicesData.length === 0 || this.isError) {
        this.isFetching = true
      } else {
        this.isTableLoading = true
      }
      this.isError = false
      try {
        // Extract unique invoice UIDs
        // Priority: parentClaim.invoices (edit mode) > parentInvoices (create mode)
        let invoiceUids
        let panelId

        if (this.parentClaim && this.parentClaim.invoices && this.parentClaim.invoices.length > 0) {
          // Edit mode: use UIDs from parentClaim.invoices (array of UID strings)
          invoiceUids = this.parentClaim.invoices.join(',')
          // Get panel ID from parentClaim
          panelId = this.parentClaim.panel?.id
        } else {
          // Create mode: extract UIDs from parentInvoices
          invoiceUids = this.parentInvoices.map(invoice => invoice.uid).join(',')
          // Get panel ID from the first invoice
          panelId = this.parentInvoices[0]?.panel?.id
        }

        const params = {
          uids: invoiceUids,
          page_size: this.pageSize,
          page: this.currentPage,
          panel: panelId
        }

        const { data } = await this.$axios.get(`/panels/${this.company.slug}/invoices`, { params })

        this.totalCount = data.count
        // Transform and store the fetched invoice data
        if (data.count > 0) {
          this.invoicesData = data.results.map(invoice => this.sanitizeData(invoice))
        } else {
          this.isError = true
          console.error('No invoices found for the selected UIDs.')
          await this.showMessage(['error', this.$t('panel.no_invoices_found')])
          this.invoicesData = []
        }
      } catch (error) {
        console.error('Error fetching invoices:', error)
        this.isError = true
        await this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
        this.isTableLoading = false
      }
    },
    onCloseDialog() {
      // Clean up synced files and files marked for deletion
      const filesToDelete = []

      // Add all synced files
      if (this.allSyncedFiles.length > 0) {
        const syncedIds = this.allSyncedFiles.map(file => file.hid).filter(Boolean)
        filesToDelete.push(...syncedIds)
      }

      // Add files marked for deletion
      if (this.fileToDelete.length > 0) {
        const deleteIds = this.fileToDelete.map(file => file.hid).filter(Boolean)
        filesToDelete.push(...deleteIds)
      }

      // Delete all files if any exist
      if (filesToDelete.length > 0) {
        // Fire and forget - delete files asynchronously without blocking dialog close
        this.$axios.post('/documents/bulk_delete', { documents: filesToDelete }).catch(error => {
          console.error('Error deleting files:', error)
        })
      }

      this.$emit('input', false)
      this.$emit('cancel')
    },
    async onSaveClaim() {
      // Validate form before proceeding
      if (!this.$refs.form.validate()) {
        await this.showMessage(['error', this.$t('panel.fill_required_fields')])
        return
      }
      if (!this.isEdit) {
        await this.handleCreateNewBulkClaim()
        return
      }

      // Edit mode: check if need to assign balance
      if (this.needToAssignBalance && !this.balanceAssigned) {
        this.showBulkAssignBalanceDialog = true
        return
      }

      // Edit mode: check if status changed
      if (this.parentClaim.status !== this.currentClaimStatus) {
        this.showChangeStatusDialog = true
        return
      }

      // Status unchanged, proceed with update
      await this.handleUpdateClaim()
    },
    // File management methods
    getDocumentIds() {
      const originalFileIds = this.originalFiles.map(file => file.hid).filter(Boolean)
      const syncedFileIds = this.allSyncedFiles.map(file => file.hid).filter(Boolean)
      return [...originalFileIds, ...syncedFileIds]
    },
    addDocumentsToPayload(payload) {
      const documentIds = this.getDocumentIds()
      if (documentIds.length > 0) {
        payload.documents = documentIds
      }
    },
    async cleanupDeletedFiles() {
      const filesToDelete = []

      // Add original files marked for deletion
      if (this.originalFileToDelete.length > 0) {
        const originalDeleteIds = this.originalFileToDelete.map(file => file.hid).filter(Boolean)
        filesToDelete.push(...originalDeleteIds)
      }

      // Add newly uploaded files marked for deletion
      if (this.fileToDelete.length > 0) {
        const deleteIds = this.fileToDelete.map(file => file.hid).filter(Boolean)
        filesToDelete.push(...deleteIds)
      }

      // Delete all files if any exist
      if (filesToDelete.length > 0) {
        try {
          await this.$axios.post('/documents/bulk_delete', { documents: filesToDelete })
        } catch (error) {
          console.error('Error deleting files:', error)
        }
      }
    },
    async handleUpdateReceivedClaim() {
      this.isLoading = true
      try {
        const payload = {
          remarks: this.editedData.remarks
        }

        this.addDocumentsToPayload(payload)

        const { data } = await this.$axios.patch(`/panels/${this.company.slug}/claims/${this.parentClaim.id}`, payload)

        await this.showMessage(['success', this.$t('panel.bulk_claim_updated')])

        await this.cleanupDeletedFiles()

        this.$emit('updated', data)

        this.showDialog = false
      } catch (error) {
        console.error('Error updating received claim:', error)
        await this.showMessage(['error', this.$t('panel.failed_update_bulk_claim')])
      } finally {
        this.isLoading = false
      }
    },
    async handleUpdateClaim(logRemarks) {
      // Special handling for received status - only update remarks and documents
      if (this.parentClaim.status === 'received') {
        await this.handleUpdateReceivedClaim()
        return
      }

      this.isLoading = true
      try {
        // Standard update logic for non-received status
        const payload = {
          status: this.currentClaimStatus,
          remarks: this.editedData.remarks
        }

        this.addDocumentsToPayload(payload)

        if (this.currentClaimStatus === 'submitted') {
          payload.submitted_amount = this.editedData.editClaimAmount.value ?? this.parentClaim.submitted_amount
          payload.submitted_date = this.editedData.date
          if (this.editedData.editClaimAmount.value !== null) {
            payload.invoices = this.editedData.editClaimAmount.invoices
          }
        }

        if (this.currentClaimStatus === 'approved') {
          payload.approved_date = this.editedData.date
          payload.approved_amount = this.finalAmountAfterDeductions
          payload.admin_fee = this.editedData.adminFee || 0
          payload.admin_fee_percentage = this.editedData.adminFeePercentage || null
          payload.rejected_amount = this.editedData.rejectedAmount || 0
          payload.rejected_date = null
          // Append invoices if balance was assigned
          if (this.balanceAssigned && this.needToAssignBalance && this.editedData.assignBalance.length > 0) {
            payload.invoices = this.editedData.assignBalance
          }
        }

        if (this.currentClaimStatus === 'rejected') {
          payload.approved_date = null
          payload.approved_amount = 0
          payload.admin_fee = 0
          payload.admin_fee_percentage = null
          payload.rejected_date = this.editedData.date
          payload.rejected_amount = this.parentClaim.submitted_amount
          payload.assign_balance = this.setAssignBalance(this.parentClaim.submitted_amount)
        }

        if (this.currentClaimStatus === 'received') {
          payload.received_date = this.editedData.date
          payload.admin_fee = this.editedData.adminFee || 0
          payload.admin_fee_percentage = this.editedData.adminFeePercentage || null
          payload.rejected_amount = this.editedData.rejectedAmount || 0
          payload.received_amount = this.finalAmountAfterDeductions
          // If transitioning from submitted to received, add approved amount and date
          if (this.parentClaim.status === 'submitted') {
            payload.approved_amount = this.parentClaim.submitted_amount - (this.editedData.rejectedAmount || 0)
            payload.approved_date = this.editedData.date
          }
          // Append invoices if balance was assigned
          if (this.balanceAssigned && this.needToAssignBalance && this.editedData.assignBalance.length > 0) {
            payload.invoices = this.editedData.assignBalance
          }
        }

        const { data } = await this.$axios.patch(`/panels/${this.company.slug}/claims/${this.parentClaim.id}`, payload)

        // Log activity for status change
        if (this.parentClaim.status !== this.currentClaimStatus) {
          const isReverting = this.currentClaimStatus === 'submitted'
          await this.$axios.post('/actions', {
            company: this.company.id,
            tag: isReverting ? 'status_reverted' : 'status_updated',
            activity: isReverting ? `Panel claim status was reverted from ${this.$options.filters.capitalize(this.parentClaim.status)} to Submitted` : `Panel claim status was updated from ${this.$options.filters.capitalize(this.parentClaim.status)} to ${this.$options.filters.capitalize(this.currentClaimStatus)}`,
            app_model: 'panel.claim',
            object_id: this.parentClaim.id,
            note: logRemarks || null
          })
        }

        // Log activity if amount was edited while status remained submitted
        if (this.currentClaimStatus === 'submitted' && this.editedData.editClaimAmount.value !== null) {
          await this.$axios.post('/actions', {
            company: this.company.id,
            tag: 'edited',
            activity: 'Panel claim amount was edited',
            app_model: 'panel.claim',
            object_id: this.parentClaim.id,
            note: this.editedData.editClaimAmount.remarks || null
          })
        }

        await this.showMessage(['success', this.$t('panel.bulk_claim_updated')])

        await this.cleanupDeletedFiles()

        this.$emit('updated', data)

        this.showDialog = false
      } catch (error) {
        console.error('Error updating claim:', error)
        await this.showMessage(['error', this.$t('panel.failed_update_bulk_claim')])
      } finally {
        this.isLoading = false
      }
    },
    async handleCreateNewBulkClaim() {
      this.isLoading = true
      try {
        // Extract invoice UIDs from the parent invoices
        const invoiceUids = this.parentInvoices.map(invoice => invoice.uid).filter(Boolean)

        // Get panel ID using effectivePanelId computed property
        const panelId = this.effectivePanelId

        if (!panelId) {
          await this.showMessage(['error', this.$t('panel.panel_info_missing')])
          return
        }

        const payload = {
          panel: panelId,
          submitted_date: this.editedData.date,
          remarks: this.editedData.remarks,
          invoices: invoiceUids
        }

        // Add documents if synced files exist
        if (this.allSyncedFiles.length > 0) {
          payload.documents = this.allSyncedFiles.map(file => file.hid).filter(Boolean)
        }

        const { data } = await this.$axios.post(`/panels/${this.company.slug}/claims`, payload)
        await this.showMessage(['success', this.$t('panel.bulk_claim_created')])

        // Clean up files marked for deletion after successful creation
        if (this.fileToDelete.length > 0) {
          const deleteIds = this.fileToDelete.map(file => file.hid).filter(Boolean)
          if (deleteIds.length > 0) {
            // Fire and forget - delete files asynchronously
            this.$axios.post('/documents/bulk_delete', { documents: deleteIds }).catch(error => {
              console.error('Error deleting files:', error)
            })
          }
        }

        this.$emit('created', data)

        this.showDialog = false
      } catch (error) {
        console.error('Error creating bulk claim:', error)
        await this.showMessage(['error', this.$t('panel.failed_create_bulk_claim')])
      } finally {
        this.isLoading = false
      }
    },
    async onChangeStatusConfirmed(logRemarks) {
      // Call handleUpdateClaim with the confirmation remarks
      await this.handleUpdateClaim(logRemarks)
    },
    onChangeStatusCanceled() {
      // Revert status back to original
      this.currentClaimStatus = this.parentClaim.status
    },
    onEditAmount() {
      this.showEditAmountDialog = true
    },
    onAssignBalance() {
      this.showBulkAssignBalanceDialog = true
    },
    onBulkAssignBalanceConfirmed(assignBalanceData) {
      console.log({ assignBalanceData })
      if (assignBalanceData && assignBalanceData.invoices) {
        this.editedData.assignBalance = assignBalanceData.invoices
        this.editedData.rejectedAmount = assignBalanceData.totalRejectedAmount
        this.editedData.approvedAmount = assignBalanceData.totalApprovedAmount
      }
      this.showBulkAssignBalanceDialog = false
    },
    onBulkAssignBalanceCanceled() {
      // Revert rejected amount to original value from parentClaim
      if (this.parentClaim) {
        this.editedData.rejectedAmount = this.parentClaim.rejected_amount || null
      }
      // Clear any balance assignments
      this.editedData.assignBalance = []
      // Close the dialog
      this.showBulkAssignBalanceDialog = false
    },
    onRejectedAmountUpdated() {
      // Clear balance assignment when rejected amount is updated
      this.editedData.assignBalance = []
    },
    onQrCreated(keyData) {
      this.databinKey = keyData.key
    },
    onQrError(error) {
      console.error('QR Code Error:', error)
      this.showMessage(['error', 'Error on QR code. Please try again later.'])
    },
    onDatabinError(error) {
      console.error('Databin Sync Error:', error)
      this.showMessage(['error', 'Error syncing files. Please try again later.'])
    },
    onUploadBatchCompleted(uploadResults) {
      // When files are uploaded, add them to the databin sync
      if (this.$refs.databinSync && uploadResults.length > 0) {
        this.$refs.databinSync.addItems(uploadResults)
      }
    },
    onDeletedFiles(deletedFiles) {
      // When files are deleted from preview, remove them from databin sync
      if (this.$refs.databinSync && deletedFiles.length > 0) {
        this.$refs.databinSync.removeItems(deletedFiles)
      }
    },
    onEditClaimAmountConfirmed(editClaimAmountData) {
      if (editClaimAmountData && editClaimAmountData.invoices) {
        this.editedData.editClaimAmount.value = editClaimAmountData.totalSubmittedAmount
        this.editedData.editClaimAmount.remarks = editClaimAmountData.remarks
        this.editedData.editClaimAmount.invoices = editClaimAmountData.invoices
      }
      this.showEditAmountDialog = false
    }
  }
}
</script>

<style scoped></style>
